import { zodResolver } from "@hookform/resolvers/zod";
import {
  Button,
  Input,
  Loader2,
  Modal,
  Search,
  User,
} from "@ews-admin/global-design-system";
import React, { useEffect, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useDebounce } from "../../../hooks/useDebounce";
import managerApi from "../../../services/manager.api";
import { dateInputToInstant } from "../../../utils/date-format-utils";
import { civilityLabel } from "../../../utils/enum-label-utils";
import {
  CreateManagerFormData,
  createManagerSchema,
} from "../../../schemas/admin.schema";
import { Manager, ManagerRequest } from "../../../types/manager.definition";
import {
  CivilityEnum,
  NotificationPrefEnum,
  RoleEnum,
} from "../../../types/user.definition";

// ── Types ──────────────────────────────────────────────────────────────────

interface AssignManagerModalProps {
  isOpen: boolean;
  onClose: () => void;
  facilityId: number;
  facilityName: string;
  onSuccess: (manager: Manager) => void;
}

type Mode = "search" | "create";

// ── Component ──────────────────────────────────────────────────────────────

export const AssignManagerModal: React.FC<AssignManagerModalProps> = ({
  isOpen,
  onClose,
  facilityId,
  facilityName,
  onSuccess,
}) => {
  const { t } = useTranslation();

  const [mode, setMode] = useState<Mode>("search");
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState<Manager[] | null>(null);
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchError, setSearchError] = useState<string | null>(null);
  const debouncedQuery = useDebounce(searchQuery, 500);

  const {
    register,
    handleSubmit,
    control,
    reset,
    formState: { errors, isValid, isDirty },
  } = useForm<CreateManagerFormData>({
    resolver: zodResolver(createManagerSchema(t)),
    mode: "onChange",
  });

  // Reset all state when modal opens/closes
  useEffect(() => {
    if (!isOpen) return;
    setMode("search");
    setSubmitError(null);
    setSearchQuery("");
    setSearchResults(null);
    setSearchError(null);
    reset();
  }, [isOpen, reset]);

  // Debounced search effect
  useEffect(() => {
    if (debouncedQuery.trim().length < 2) {
      setSearchResults(null);
      setSearchError(null);
      return;
    }
    setSearchLoading(true);
    setSearchError(null);
    managerApi
      .searchManagers(debouncedQuery.trim())
      .then(results => setSearchResults(results))
      .catch(() => {
        setSearchError(t("admin.facilities.assignManagerModal.loadError"));
        setSearchResults(null);
      })
      .finally(() => setSearchLoading(false));
  }, [debouncedQuery, t]);

  const handleAssignExisting = async (manager: Manager) => {
    setSubmitting(true);
    setSubmitError(null);
    try {
      await managerApi.assignManagerToFacility(facilityId, manager.id);
      onSuccess(manager);
      onClose();
    } catch {
      setSubmitError(t("admin.facilities.assignManagerModal.error"));
    } finally {
      setSubmitting(false);
    }
  };

  const handleCreate = async (data: CreateManagerFormData) => {
    setSubmitting(true);
    setSubmitError(null);

    const request: ManagerRequest = {
      firstName: data.firstName,
      lastName: data.lastName,
      email: data.email,
      phoneNumber: data.phoneNumber,
      civility: data.civility,
      ...(dateInputToInstant(data.dateOfBirth)
        ? { dateOfBirth: dateInputToInstant(data.dateOfBirth) }
        : {}),
      roleName: RoleEnum.MANAGER,
      preferredNotificationMethods: [NotificationPrefEnum.EMAIL],
      photoUrl: "",
      healthcareFacilityId: facilityId,
    };

    try {
      const created = await managerApi.createManager(request);
      onSuccess(created);
      onClose();
    } catch {
      setSubmitError(t("admin.createManager.error"));
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    if (submitting) return;
    onClose();
  };

  const switchToCreate = () => {
    reset();
    setSubmitError(null);
    setMode("create");
  };

  const switchToSearch = () => {
    setSubmitError(null);
    setMode("search");
  };

  // ── Search mode ──────────────────────────────────────────────────────────

  if (mode === "search") {
    return (
      <Modal
        isOpen={isOpen}
        onClose={handleClose}
        title={t("admin.facilities.assignManagerModal.title")}
        size="lg"
        variant="info"
      >
        <div className="p-6 space-y-4">
          <p className="text-sm text-neutral-600">
            {t("admin.facilities.assignManagerModal.subtitle", {
              facilityName,
            })}
          </p>

          {/* Search input */}
          <div className="relative">
            <div className="pointer-events-none absolute inset-y-0 left-3 flex items-center">
              <Search className="h-4 w-4 text-gray-400" />
            </div>
            <input
              type="text"
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              placeholder={t("admin.facilities.assignManagerModal.search")}
              className="w-full rounded-lg border border-gray-300 py-2.5 pl-10 pr-4 text-sm focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500"
              autoFocus
            />
          </div>

          {/* Loading */}
          {searchLoading && (
            <div className="flex items-center gap-2 text-sm text-neutral-500">
              <Loader2 className="h-4 w-4 animate-spin" />
              <span>{t("admin.facilities.assignManagerModal.searching")}</span>
            </div>
          )}

          {/* Search error */}
          {searchError && !searchLoading && (
            <p className="text-sm text-danger-600">{searchError}</p>
          )}

          {/* Results */}
          {!searchLoading && !searchError && searchResults !== null && (
            <>
              {searchResults.length === 0 ? (
                <div className="space-y-3 rounded-lg border border-neutral-200 p-4 text-center">
                  <p className="text-sm text-neutral-600">
                    {t("admin.facilities.assignManagerModal.noManagers")}
                  </p>
                  <Button variant="outline" onClick={switchToCreate}>
                    {t("admin.facilities.assignManagerModal.createNew")}
                  </Button>
                </div>
              ) : (
                <ul className="divide-y divide-neutral-100 rounded-lg border border-neutral-200 overflow-hidden max-h-64 overflow-y-auto">
                  {searchResults.map(m => (
                    <li
                      key={m.id}
                      className="flex cursor-pointer items-start gap-3 p-4 hover:bg-primary-50 transition-colors"
                      onClick={() => !submitting && handleAssignExisting(m)}
                    >
                      <User className="mt-0.5 h-5 w-5 flex-shrink-0 text-primary-500" />
                      <div className="min-w-0 flex-1">
                        <p className="truncate font-medium text-neutral-900">
                          {civilityLabel(t, m.civility)} {m.firstName}{" "}
                          {m.lastName}
                        </p>
                        <p className="mt-0.5 truncate text-sm text-neutral-500">
                          {m.email}
                        </p>
                        <p className="mt-0.5 truncate text-xs text-neutral-400">
                          {m.healthcareFacility?.name ??
                            t("admin.facilities.assignManagerModal.noFacility")}
                        </p>
                      </div>
                      {submitting && (
                        <Loader2 className="h-4 w-4 animate-spin text-primary-500 flex-shrink-0 mt-1" />
                      )}
                    </li>
                  ))}
                </ul>
              )}
            </>
          )}

          {/* Hint when no search yet */}
          {!searchLoading && searchResults === null && !searchError && (
            <div className="space-y-3">
              <p className="text-sm text-neutral-500">
                {t("admin.facilities.assignManagerModal.searchHint")}
              </p>
              <Button variant="outline" onClick={switchToCreate}>
                {t("admin.facilities.assignManagerModal.createNew")}
              </Button>
            </div>
          )}

          {submitError && (
            <p className="text-sm text-danger-600">{submitError}</p>
          )}
        </div>

        <div className="flex justify-end px-6 py-4 border-t border-neutral-200">
          <Button
            type="button"
            variant="outline"
            onClick={handleClose}
            disabled={submitting}
          >
            {t("common.cancel")}
          </Button>
        </div>
      </Modal>
    );
  }

  // ── Create mode ──────────────────────────────────────────────────────────

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title={t("admin.createManager.title")}
      size="lg"
      variant="info"
    >
      <form onSubmit={handleSubmit(handleCreate)}>
        <div className="p-6 space-y-4">
          {/* Back to search */}
          <button
            type="button"
            className="flex items-center gap-1 text-sm text-primary-600 hover:text-primary-700 hover:underline"
            onClick={switchToSearch}
          >
            {t("admin.createManager.backToList")}
          </button>

          <p className="text-sm text-neutral-600">
            {t("admin.createManager.subtitle", { facilityName })}
          </p>

          {submitError && (
            <p className="text-sm text-danger-600">{submitError}</p>
          )}

          <div className="grid grid-cols-2 gap-3">
            <Input
              label={t("admin.createManager.firstName")}
              placeholder={t("admin.createManager.firstNamePlaceholder")}
              required
              error={errors.firstName?.message}
              {...register("firstName")}
            />
            <Input
              label={t("admin.createManager.lastName")}
              placeholder={t("admin.createManager.lastNamePlaceholder")}
              required
              error={errors.lastName?.message}
              {...register("lastName")}
            />
          </div>

          <Input
            label={t("admin.createManager.email")}
            placeholder={t("admin.createManager.emailPlaceholder")}
            type="email"
            required
            error={errors.email?.message}
            {...register("email")}
          />

          <Input
            label={t("admin.createManager.phoneNumber")}
            placeholder={t("admin.createManager.phoneNumberPlaceholder")}
            type="tel"
            required
            error={errors.phoneNumber?.message}
            {...register("phoneNumber")}
          />

          <Input
            label={t("admin.createManager.dateOfBirth")}
            type="date"
            error={errors.dateOfBirth?.message}
            {...register("dateOfBirth")}
          />

          <div className="flex flex-col gap-1">
            <label className="text-sm font-medium text-neutral-700">
              {t("admin.createManager.civility")}
            </label>
            <Controller
              name="civility"
              control={control}
              defaultValue={CivilityEnum.MR}
              render={({ field }) => (
                <select
                  {...field}
                  className="px-3 py-2 w-full text-sm rounded-lg border border-neutral-300 focus:outline-none focus:ring-2 focus:ring-primary-500"
                >
                  <option value={CivilityEnum.MR}>{t("common.mr")}</option>
                  <option value={CivilityEnum.MRS}>{t("common.mrs")}</option>
                </select>
              )}
            />
            {errors.civility && (
              <p className="mt-1 text-sm text-danger-600">
                {errors.civility.message}
              </p>
            )}
          </div>
        </div>

        <div className="flex gap-3 justify-end px-6 py-4 border-t border-neutral-200">
          <Button
            type="button"
            variant="outline"
            onClick={handleClose}
            disabled={submitting}
          >
            {t("common.cancel")}
          </Button>
          <Button type="submit" disabled={!isValid || !isDirty || submitting}>
            {submitting
              ? t("common.processing")
              : t("admin.createManager.submit")}
          </Button>
        </div>
      </form>
    </Modal>
  );
};
